/** How a document is shown: zoom and the saved way-up (Jim 11/2, BMP maps
 *  photographed sideways). Rotation is quarter turns; flip mirrors left-right. */
export type Orientation = { rotation: number; flip: boolean };

export type ViewState = Orientation & { scale: number };

export const SCALE_STEPS = [0.5, 0.75, 1, 1.5, 2, 3, 4] as const;

export function normalizeRotation(deg: number): number {
  const r = Math.round(deg / 90) * 90;
  return ((r % 360) + 360) % 360;
}

export function turn(state: ViewState, direction: 'left' | 'right'): ViewState {
  return {
    ...state,
    rotation: normalizeRotation(
      state.rotation + (direction === 'right' ? 90 : -90)
    ),
  };
}

export function zoom(state: ViewState, direction: 'in' | 'out'): ViewState {
  const i = SCALE_STEPS.findIndex((s) => s >= state.scale - 1e-6);
  const at = i === -1 ? SCALE_STEPS.length - 1 : i;
  const next =
    direction === 'in'
      ? Math.min(at + 1, SCALE_STEPS.length - 1)
      : Math.max(at - 1, 0);
  return { ...state, scale: SCALE_STEPS[next] };
}

export function transformFor(state: ViewState): string {
  return `rotate(${state.rotation}deg) scale(${state.scale}) scaleX(${state.flip ? -1 : 1})`;
}

export function orientationOf(
  doc: { rotation?: number | null; flip?: boolean | null } | null | undefined
): Orientation {
  return { rotation: normalizeRotation(doc?.rotation ?? 0), flip: !!doc?.flip };
}

export function sameOrientation(a: Orientation, b: Orientation): boolean {
  return (
    normalizeRotation(a.rotation) === normalizeRotation(b.rotation) &&
    !!a.flip === !!b.flip
  );
}
